import { isValid } from "date-fns";
import { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

import type {
  AssetBookingDetailDto,
  BookableAssetDeletedResult,
  BookableAssetDetailDto,
  BookableSlotDto,
  CreatedEntityDto,
} from "@/api/types";
import { AudiencePreview } from "@/components/AudienceSelector/AudiencePreview";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { EntityDetailsCard } from "@/components/EntityDetailsCard/EntityDetailsCard";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormInput } from "@/components/Form/FormInput";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CurrencyEuroIcon } from "@/components/Icons/CurrencyEuroIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { File06Icon } from "@/components/Icons/File06Icon";
import { LayerSingleIcon } from "@/components/Icons/LayerSingleIcon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { SectionCard } from "@/design-system/SectionCard";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { dayOfWeekIndex } from "@/helpers/date";
import { isContentAutoTranslated } from "@/helpers/languages";
import { canManageAnyBookableAsset } from "@/helpers/permissions";
import { createRequiredStringRule } from "@/helpers/rules";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { BookingTimeslotSelector } from "@/modules/bookings/components/BookingTimeslotSelector";
import { daysOptions } from "@/modules/bookings/constants";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { getIsBookableAssetSharable } from "../../helpers";
import { BookingSuccessModal } from "./components/BookingSuccessModal";

const MAX_LENGTH_REASON = 256;
export interface BookingFormProps {
  assetDetails: BookableAssetDetailDto;
  assetSlots: BookableSlotDto[];
  isLoadingSlots: boolean;
  bookingDetails: AssetBookingDetailDto | undefined;
  minDate: Date;
  maxDate: Date;
  defaultValues: DefaultCreateOrEditBookingFormValues;
  onDateChange: (date: Date | undefined) => void;
  onSubmit: (data: CreateOrEditBookingFormValues) => Promise<CreatedEntityDto>;
  futureBookings: number;
  isSubmitting: boolean;
  onDelete: (id: string) => Promise<BookableAssetDeletedResult>;
}
export interface CreateOrEditBookingFormValues {
  date: Date;
  bookAllDay: boolean;
  startTime: number | null;
  endTime: number | null;
  reason?: string;
  isRegulationAccepted: boolean;
}
type DefaultCreateOrEditBookingFormValues = Omit<CreateOrEditBookingFormValues, "bookAllDay" | "date"> & {
  date: Date | undefined;
};
export function BookingForm({
  assetDetails,
  assetSlots,
  bookingDetails,
  minDate,
  maxDate,
  defaultValues,
  isLoadingSlots,
  onDateChange,
  onSubmit,
  futureBookings,
  isSubmitting,
  onDelete,
}: BookingFormProps): React.ReactNode {
  const slug = useSlug();
  const { t, i18n } = useTranslation();
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();
  const [isSuccessModalOpened, successModalOpenHandler] = useBool(false);
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-asset-modal");
  const form = useForm<CreateOrEditBookingFormValues>({
    defaultValues,
    mode: "onSubmit",
  });
  const bookAllDay = useWatch({
    control: form.control,
    name: "bookAllDay",
  });
  useEffect(() => {
    form.register("startTime", {
      validate: {
        required: (currStartTime, currFormValues) => {
          const currEndTime = currFormValues.endTime;
          if (!bookAllDay && !isDefined(currStartTime))
            return t("page.bookings.book-asset.form.booking-timeslot.error");
          if (isDefined(currStartTime) && !isDefined(currEndTime))
            return t("page.bookings.book-asset.form.booking-timeslot.error");

          return undefined;
        },
      },
    });
  }, [form, t, bookAllDay]);
  const isDayDisabled = useCallback(
    (current: Date) => {
      const disabledDays = assetDetails.bookableDays
        .filter((bookableDay) => !bookableDay.enabled)
        .map((bookableDay) => daysOptions.indexOf(bookableDay.day));

      return disabledDays.includes(dayOfWeekIndex(current));
    },
    [assetDetails.bookableDays],
  );
  const handleDateChange = (date: Date | undefined) => {
    onDateChange(date);
    form.setValue("startTime", null);
    form.setValue("endTime", null);
    form.setValue("bookAllDay", false);
  };
  const handleSubmit = async (values: CreateOrEditBookingFormValues) => {
    try {
      await onSubmit(values);
    } catch {
      // The mutation displays the error. Consume the rejection at the submit boundary.
      return;
    }
    successModalOpenHandler.setTrue();
  };
  const actions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      value: "edit",
      type: "link",
      icon: <Edit05Icon />,
      href: routes.bookings.editAsset({
        slug,
        id: assetDetails.id,
      }),
      isHidden: !assetDetails.canEdit,
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      type: "button",
      callbackFn: () => openDeleteModal(assetDetails.id),
      isHidden: !assetDetails.canDelete,
    },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={assetDetails.name}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.bookings.list-assets.title"),
              to: routes.bookings.list({
                slug,
              }),
            },
            {
              name: assetDetails.name,
            },
          ]}
        />
      }
    >
      <PageGrid.Row>
        <PageGrid.Column size="50%">
          <EntityDetailsCard
            title={assetDetails.name}
            description={assetDetails.description}
            images={assetDetails.images}
            renderBadges={() => assetDetails.hasAudience && sessionUser.isAdmin && <Badge icon={<UsersCheckIcon />} />}
            infoItems={[
              {
                icon: <LayerSingleIcon />,
                label: t("page.bookings.book-asset.label.size"),
                value: assetDetails.sizeSpecification
                  ? t("page.bookings.book-asset.label.size-format", { size: assetDetails.sizeSpecification })
                  : undefined,
              },
              {
                icon: <CurrencyEuroIcon />,
                label: t("page.bookings.book-asset.label.price"),
                value: assetDetails.pricePerHourSpecification
                  ? t("page.bookings.book-asset.label.price-format", {
                      price: assetDetails.pricePerHourSpecification,
                    })
                  : undefined,
              },
              {
                icon: <Users01Icon />,
                label: t("page.bookings.book-asset.label.capacity"),
                value: assetDetails.capacitySpecification,
              },
              {
                icon: <MarkerPin01Icon />,
                label: t("page.bookings.book-asset.label.location"),
                value: assetDetails.locationSpecification,
              },
            ]}
            renderBody={
              assetDetails.audience &&
              assetDetails.audience.length > 0 &&
              sessionUser.isAdmin &&
              hasPermission(canManageAnyBookableAsset)
                ? () => (
                    <div className="w-fit">
                      <AudiencePreview audience={assetDetails.audience} readOnly />
                    </div>
                  )
                : undefined
            }
            renderFooter={
              isContentAutoTranslated(assetDetails.originalLanguageId, sessionUser)
                ? () => <AutoTranslatedLabel />
                : undefined
            }
            actions={actions}
            entityId={assetDetails.id}
            entityType="bookableAsset"
            allowShare={getIsBookableAssetSharable(assetDetails)}
          />
        </PageGrid.Column>
        <PageGrid.Column size="50%">
          <SectionCard title={t("page.bookings.book-asset.section.book-asset.title")}>
            {maxDate < minDate && <p>{t("page.bookings.book-asset.section.book-asset.not-available")}</p>}
            {maxDate >= minDate && (
              <Form.Root formMethods={form} onSubmit={handleSubmit}>
                <Form.Body>
                  <FormField label={t("page.bookings.book-asset.form.booking-date.label")}>
                    <FormDatePicker<CreateOrEditBookingFormValues, "date">
                      data-testid="date-picker-input"
                      name="date"
                      min={minDate}
                      max={maxDate}
                      disabledDate={isDayDisabled}
                      onChange={handleDateChange}
                      rules={{
                        validate: {
                          validDate: (date) =>
                            isValid(date) ? undefined : t("components.form.error.required-generic"),
                          laterThanMin: (date) => {
                            if (!date) {
                              return undefined;
                            }

                            return date < minDate
                              ? t("page.bookings.book-asset.form.booking-date.error.must-be-after-date", {
                                  date: formatDate(i18n, "date", minDate),
                                })
                              : undefined;
                          },
                          soonerThanMax: (date) => {
                            if (!date) {
                              return undefined;
                            }
                            if (!maxDate) {
                              return;
                            }

                            return date > maxDate
                              ? t("page.bookings.book-asset.form.booking-date.error.must-be-before-date", {
                                  date: formatDate(i18n, "date", maxDate),
                                })
                              : undefined;
                          },
                        },
                      }}
                    />
                  </FormField>
                  {isLoadingSlots && (
                    <div className="mx-auto mb-space-md flex justify-center">
                      <Spinner size="xl" />
                    </div>
                  )}
                  {!isLoadingSlots && assetSlots.length === 0 && (
                    <span className="text-body">
                      {t("page.bookings.book-asset.form.booking-timeslot.no-slots.available")}
                    </span>
                  )}
                  {!isLoadingSlots && assetSlots.length > 0 && (
                    <>
                      <FormErrorWrapper name="startTime" encircle>
                        <BookingTimeslotSelector
                          timeslot={assetDetails.timeslot}
                          timeslots={assetSlots}
                          booking={bookingDetails}
                          isBookMultipleTimeslotsAllowed={assetDetails.canBookMultipleSlots}
                        />
                      </FormErrorWrapper>
                      {assetDetails.requireBookingReason && (
                        <FormField label={t("page.bookings.book-asset.form.booking-reason.label")} required>
                          <FormInput<CreateOrEditBookingFormValues, "reason">
                            name="reason"
                            placeholder={t("page.bookings.book-asset.form.booking-reason.placeholder")}
                            rules={{
                              validate: {
                                required: createRequiredStringRule(
                                  t,
                                  "page.bookings.book-asset.form.booking-reason.name",
                                ),
                              },
                              maxLength: {
                                message: t("components.form.error.max-length", {
                                  length: MAX_LENGTH_REASON,
                                }),
                                value: MAX_LENGTH_REASON,
                              },
                            }}
                          />
                        </FormField>
                      )}
                      {assetDetails.regulationDocument && (
                        <FormErrorWrapper name="isRegulationAccepted" encircle>
                          <Stack gap="xs">
                            <FormCheckbox
                              name="isRegulationAccepted"
                              label={t("page.bookings.book-asset.form.booking-regulations.label")}
                              alignTop
                              rules={{
                                validate: {
                                  needsChecked: (x) => {
                                    if (!x) {
                                      return t("page.bookings.book-asset.form.booking-regulations.label");
                                    }
                                  },
                                },
                              }}
                            />
                            <Button
                              variant="subtle"
                              onClick={() => window.open(assetDetails.regulationDocument?.url, "_blank")}
                              icon={<File06Icon size={20} />}
                            >
                              <span className="truncate">{assetDetails.regulationDocument?.fileName}</span>
                            </Button>
                          </Stack>
                        </FormErrorWrapper>
                      )}
                    </>
                  )}
                </Form.Body>
                {!isLoadingSlots && assetSlots.length > 0 && (
                  <Form.Footer
                    renderPrimaryAction={() => (
                      <Button type="submit" isLoading={isSubmitting}>
                        {t("page.bookings.book-asset.book")}
                      </Button>
                    )}
                  />
                )}
              </Form.Root>
            )}
          </SectionCard>
        </PageGrid.Column>
      </PageGrid.Row>
      <BookingSuccessModal isOpened={isSuccessModalOpened} onOpenChange={successModalOpenHandler.set} />
      <DeleteModal
        title={t("page.bookings.delete-asset.modal.title")}
        description={
          futureBookings === 0
            ? t("page.bookings.delete-asset.modal.description.no-bookings")
            : t("page.bookings.delete-asset.modal.description", {
                count: futureBookings,
              })
        }
        onDelete={onDelete}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </DocumentPage>
  );
}
